import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Minimal non-blocking toast system (UX-10). Replaces the `window.alert`
 * that `pages/reports/spend.tsx` used for export errors.
 *
 * Stack of up to 3, bottom-right, auto-dismissing after 5s (8s for
 * errors), each with a manual close button.
 */
type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const MAX_TOASTS = 3;
const DURATION_MS: Record<ToastTone, number> = {
  success: 5_000,
  info: 5_000,
  error: 8_000,
};

let nextToastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = ++nextToastId;
      setToasts((prev) => {
        const next = [...prev, { id, tone, message }];
        // Keep only the newest MAX_TOASTS — older ones roll off the stack.
        return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
      });
      const timer = setTimeout(() => dismiss(id), DURATION_MS[tone]);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  // Clear any outstanding timers on unmount (e.g. route/provider teardown
  // in tests) so they don't fire against an unmounted tree.
  useEffect(() => {
    const timersMap = timers.current;
    return () => {
      timersMap.forEach((t) => clearTimeout(t));
      timersMap.clear();
    };
  }, []);

  const api: ToastApi = {
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-full max-w-sm pointer-events-none"
            data-testid="toast-stack"
          >
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// Card + icons
// ---------------------------------------------------------------------------

const TONE_STYLE: Record<ToastTone, string> = {
  success: 'border-success-100 bg-success-50 text-success-700',
  error: 'border-danger-100 bg-danger-50 text-danger-700',
  info: 'border-primary-100 bg-primary-50 text-primary-700',
};

const ToastCard: React.FC<{ toast: ToastItem; onDismiss: () => void }> = ({ toast, onDismiss }) => (
  <div
    role={toast.tone === 'error' ? 'alert' : 'status'}
    aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
    data-testid={`toast-${toast.tone}`}
    className={
      'pointer-events-auto flex items-start gap-2 rounded-lg border shadow-card px-3 py-2.5 text-sm ' +
      TONE_STYLE[toast.tone]
    }
  >
    <ToastIcon tone={toast.tone} />
    <span className="flex-1 min-w-0">{toast.message}</span>
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      data-testid="toast-dismiss"
      className="shrink-0 text-current opacity-60 hover:opacity-100 transition-opacity"
    >
      <CloseIcon />
    </button>
  </div>
);

const ICON = 'h-4 w-4 shrink-0 mt-0.5';

function ToastIcon({ tone }: { tone: ToastTone }) {
  if (tone === 'error') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </svg>
    );
  }
  if (tone === 'success') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12.5 2.5 2.5 4.5-5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={ICON} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export default ToastProvider;
